import { HttpErrorResponse } from '@angular/common/http';
import type { IResponse } from '../models/api-response.model';

export class AppError extends Error {
  readonly handled: boolean;

  constructor(
    public readonly code: string,
    message: string,
    public readonly statusCode: number,
    public override readonly cause?: unknown,
    handled = false,
  ) {
    super(message);
    this.name = 'AppError';
    this.handled = handled;
  }

  markHandled(): AppError {
    return new AppError(this.code, this.message, this.statusCode, this.cause, true);
  }

  static from(err: unknown): AppError {
    if (err instanceof AppError) {
      return err;
    }

    if (err instanceof HttpErrorResponse) {
      const envelope = err.error as Partial<IResponse<unknown>> | undefined;
      const code =
        envelope?.errorCode ??
        (err.status === 0
          ? 'NETWORK_ERROR'
          : err.status >= 500
            ? 'SERVER_ERROR'
            : `HTTP_${err.status}`);
      // Only a backend-supplied `statusMessage` is safe to surface verbatim. When it's absent the
      // display layer (errorInterceptor toast, AppErrorHandler) supplies a localized fallback, so
      // leave `message` empty rather than hardcoding English here.
      const message = envelope?.statusMessage ?? '';
      return new AppError(code, message, err.status, err);
    }

    if (err instanceof Error) {
      return new AppError('UNKNOWN', err.message, 0, err);
    }

    return new AppError('UNKNOWN', '', 0, err);
  }
}
